import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import formaEntregaService, { updateFormaEntrega, deleteFormaEntrega } from '../../services/formaEntregaService';

const initialState = {
  nome: '',
  status: '',
};

const FormaEntregaEdit = () => {
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadFormaEntrega = async () => {
      try {
        console.log('Carregando forma de entrega, ID:', id);
        const response = await formaEntregaService.getById(id);
        console.log('Resposta da API:', response);
        
        // Verificação defensiva para diferentes estruturas de resposta
        let entrega = null;
        if (response) {
          if (response.success && response.data) {
            entrega = response.data;
          } else if (response.data) {
            entrega = response.data;
          } else if (Array.isArray(response)) {
            entrega = response.find(f => String(f.idFormaEntrega) === String(id));
          } else if (response.idFormaEntrega) {
            entrega = response;
          }
        }
        
        console.log('Dados da entrega processados:', entrega);
        if (entrega) {
          setForm({ nome: entrega.nome || '', status: entrega.status || 'A' });
        } else {
          setError('Forma de entrega não encontrada');
        }
      } catch (error) {
        console.error('Erro ao carregar forma de entrega:', error);
        setError('Erro ao carregar dados da forma de entrega');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadFormaEntrega();
    } else {
      setLoading(false);
      setError('ID não fornecido');
    }
  }, [id]);

  const validate = () => {
    if (!form.nome.trim()) return 'O nome é obrigatório.';
    if (!form.status) return 'O status é obrigatório.';
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  if (loading) return <div className="max-w-lg mx-auto mt-10 text-blue-600">Carregando dados...</div>;
  if (deleteSuccess) return <div className="max-w-lg mx-auto mt-10 text-green-600">Forma de entrega excluída com sucesso!</div>;

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Forma de Entrega</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={50}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Nome da forma de entrega"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status *</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione o status</option>
            <option value="Ativo">Ativo</option>
            <option value="Inativo">Inativo</option>
          </select>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Forma de entrega atualizada com sucesso!</div>}
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/forma-entrega')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={handleDelete} disabled={deleting} aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Edição"
        message="Deseja realmente salvar as alterações desta forma de entrega?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          setLoading(true);
          setError('');
          setSuccess(false);
          try {
            await updateFormaEntrega(id, form);
            setSuccess(true);
            setTimeout(() => navigate('/forma-entrega'), 1200);
          } catch (err) {
            setError('Erro ao atualizar forma de entrega.');
          }
          setLoading(false);
        }}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir esta forma de entrega? Esta ação não poderá ser desfeita."
        confirmType="delete"
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          setDeleting(true);
          try {
            await deleteFormaEntrega(id);
            setDeleteSuccess(true);
            setTimeout(() => navigate('/forma-entrega'), 1200);
          } catch (err) {
            setError('Erro ao excluir forma de entrega.');
          }
          setDeleting(false);
        }}
      />
    </div>
  );
};

export default FormaEntregaEdit;
